/**
 * Analytics Management Component - Admin Dashboard
 * Displays comprehensive analytics and insights
 */

import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  IndianRupee,
  ShoppingCart,
  Package,
  Users,
  Loader2,
  AlertCircle,
  RefreshCw,
  Clock,
  CheckCircle,
  Truck,
  XCircle,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Calendar,
  FileSpreadsheet
} from 'lucide-react';
import analyticsService from '../../services/analyticsService';
import reportService from '../../services/reportService';
import batchService from '../../services/batchService';

const AnalyticsManagement = () => {
  const [analytics, setAnalytics] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [lossData, setLossData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Report download state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportStartDate, setReportStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [reportEndDate, setReportEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError('');

    try {
      const [analyticsResult, orderStatsResult, userStatsResult, lossResult] = await Promise.all([
        analyticsService.getAnalytics(),
        analyticsService.getOrderStats(),
        analyticsService.getUserStats(),
        batchService.getLossAnalytics().then(data => ({ success: true, data })).catch(() => ({ success: false })),
      ]);

      if (analyticsResult.success) {
        setAnalytics(analyticsResult.data);
      }
      if (orderStatsResult.success) {
        setOrderStats(orderStatsResult.data);
      }
      if (userStatsResult.success) {
        setUserStats(userStatsResult.data);
      }
      if (lossResult.success) {
        setLossData(lossResult.data);
      }

      if (!analyticsResult.success && !orderStatsResult.success) {
        setError('Failed to load analytics data');
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setReportLoading(true);
    try {
      const result = await reportService.getAnalyticsReport(reportStartDate, reportEndDate);
      if (result.success) {
        const filename = `analytics-report-${reportStartDate}-to-${reportEndDate}.csv`;
        reportService.downloadAnalyticsCSV(result.data, filename);
        setShowReportModal(false);
      } else {
        alert(result.error || 'Failed to generate report');
      }
    } catch (err) {
      console.error('Report download error:', err);
      alert('Failed to download report');
    } finally {
      setReportLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'processing': return <Package className="w-4 h-4" />;
      case 'shipped': return <Truck className="w-4 h-4" />;
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'processing': return 'bg-blue-100 text-blue-700';
      case 'shipped': return 'bg-purple-100 text-purple-700';
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#548B2E]" />
          <p className="text-gray-500">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error && !analytics && !orderStats) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-red-700 font-medium">{error}</p>
        <button 
          onClick={fetchAllData}
          className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const comparison = analytics?.comparison || {};
  const revenueChange = comparison.revenueChange || 0;
  const ordersChange = comparison.ordersChange || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Analytics</h1>
          <p className="text-gray-600">Overview of sales performance and trends</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowReportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] transition-colors"
          >
            <Download className="w-4 h-4" />
            Download Report
          </button>
          <button
            onClick={fetchAllData}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Revenue */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <IndianRupee className="w-6 h-6 text-green-600" />
            </div>
            {revenueChange !== 0 && (
              <span className={`flex items-center text-sm font-medium ${revenueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {revenueChange >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                {Math.abs(revenueChange)}%
              </span>
            )}
          </div>
          <h3 className="text-gray-500 text-sm font-medium">Total Revenue</h3>
          <p className="text-2xl font-bold text-gray-800 mt-1">
            {formatCurrency(orderStats?.revenue || 0)}
          </p>
          <p className="text-xs text-gray-400 mt-2">vs last week</p>
        </div>

        {/* Total Orders */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <ShoppingCart className="w-6 h-6 text-blue-600" />
            </div>
            {ordersChange !== 0 && (
              <span className={`flex items-center text-sm font-medium ${ordersChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {ordersChange >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                {Math.abs(ordersChange)}%
              </span>
            )}
          </div>
          <h3 className="text-gray-500 text-sm font-medium">Total Orders</h3>
          <p className="text-2xl font-bold text-gray-800 mt-1">
            {formatNumber(orderStats?.total || 0)}
          </p>
          <p className="text-xs text-gray-400 mt-2">All time</p>
        </div>

        {/* Pending Orders */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <h3 className="text-gray-500 text-sm font-medium">Pending Orders</h3>
          <p className="text-2xl font-bold text-gray-800 mt-1">
            {formatNumber(orderStats?.pending || 0)}
          </p>
          <p className="text-xs text-gray-400 mt-2">Needs attention</p>
        </div>

        {/* Total Users */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <h3 className="text-gray-500 text-sm font-medium">Total Users</h3>
          <p className="text-2xl font-bold text-gray-800 mt-1">
            {formatNumber(userStats?.total || 0)}
          </p>
          <p className="text-xs text-gray-400 mt-2">{userStats?.newThisMonth || 0} new this month</p>
        </div>

        {/* Expiry Loss (30 days) */}
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-400">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <h3 className="text-gray-500 text-sm font-medium">Expiry Loss (30d)</h3>
          <p className="text-2xl font-bold text-red-600 mt-1">
            {formatCurrency(lossData?.last30Days?.totalLoss || 0)}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            {lossData?.last30Days?.totalUnits || 0} units across {lossData?.last30Days?.productsAffected || 0} product(s)
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            Revenue Trend (Last 30 Days)
          </h2>
          {analytics?.dailyRevenue?.length > 0 ? (
            <div className="space-y-3">
              {/* Simple bar chart visualization */}
              <div className="h-48 flex items-end gap-1">
                {analytics.dailyRevenue.slice(-14).map((day) => {
                  const maxRevenue = Math.max(...analytics.dailyRevenue.map(d => d.revenue));
                  const height = maxRevenue > 0 ? (day.revenue / maxRevenue) * 100 : 0;
                  return (
                    <div 
                      key={day.date} 
                      className="flex-1 flex flex-col items-center gap-1"
                      title={`${(() => { const date = new Date(day.date); const offset = date.getTimezoneOffset() * 60000; return new Date(date.getTime() + offset).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); })()}: ${formatCurrency(day.revenue)}`}
                    >
                      <div 
                        className="w-full bg-green-500 rounded-t transition-all hover:bg-green-600 cursor-pointer"
                        style={{ height: `${Math.max(height, 2)}%` }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>14 days ago</span>
                <span>Today</span>
              </div>
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-xs text-gray-500">This Week</p>
                  <p className="text-lg font-semibold text-gray-800">{formatCurrency(comparison.thisWeek?.revenue || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Last Week</p>
                  <p className="text-lg font-semibold text-gray-800">{formatCurrency(comparison.lastWeek?.revenue || 0)}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No revenue data available</p>
              </div>
            </div>
          )}
        </div>

        {/* Order Status Distribution */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            Order Status Distribution
          </h2>
          <div className="space-y-4">
            {orderStats && (
              <>
                {[
                  { status: 'pending', count: orderStats.pending, color: 'bg-yellow-500' },
                  { status: 'processing', count: orderStats.processing, color: 'bg-blue-500' },
                  { status: 'shipped', count: orderStats.shipped, color: 'bg-purple-500' },
                  { status: 'delivered', count: orderStats.delivered, color: 'bg-green-500' },
                  { status: 'cancelled', count: orderStats.cancelled, color: 'bg-red-500' },
                ].map(({ status, count, color }) => {
                  const percentage = orderStats.total > 0 ? (count / orderStats.total) * 100 : 0;
                  return (
                    <div key={status} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 capitalize text-gray-700">
                          {getStatusIcon(status)}
                          {status}
                        </span>
                        <span className="text-gray-500">{count} ({percentage.toFixed(1)}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${color} rounded-full transition-all`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#548B2E]" />
            Top Selling Products
          </h2>
          {analytics?.topProducts?.length > 0 ? (
            <div className="space-y-3">
              {analytics.topProducts.slice(0, 5).map((product, idx) => (
                <div key={product.productId} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="flex items-center justify-center w-6 h-6 bg-[#548B2E] text-white rounded-full text-xs font-bold">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{product.productName}</p>
                    <p className="text-sm text-gray-500">{formatNumber(product.totalQuantity)} units sold</p>
                  </div>
                  <p className="font-semibold text-gray-700">{formatCurrency(product.totalRevenue)}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No product data available</p>
              </div>
            </div>
          )}
        </div>

        {/* Slow Movers */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2">
            <ArrowDownRight className="w-5 h-5 text-red-400" />
            Slow Moving Products
          </h2>
          <p className="text-xs text-gray-400 mb-4">Least sold in the last 30 days</p>
          {analytics?.leastSoldProducts?.length > 0 ? (
            <div className="space-y-3">
              {analytics.leastSoldProducts.slice(0, 5).map((product) => {
                const isLowStock = product.currentStock !== null && product.currentStock <= 5;
                const isOverstocked = product.currentStock !== null && product.currentStock > 20 && product.totalQuantity <= 2;
                return (
                  <div key={product.productId} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-800 truncate">{product.productName}</p>
                        {isLowStock && (
                          <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded-full font-medium whitespace-nowrap">
                            Low Stock
                          </span>
                        )}
                        {isOverstocked && (
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-600 text-xs rounded-full font-medium whitespace-nowrap">
                            Overstocked
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <p className="text-sm text-gray-500">{formatNumber(product.totalQuantity)} units sold</p>
                        {product.currentStock !== null && (
                          <p className="text-xs text-gray-400">{product.currentStock} in stock</p>
                        )}
                      </div>
                    </div>
                    <p className="font-semibold text-gray-700 text-sm">{formatCurrency(product.totalRevenue)}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No product data available</p>
              </div>
            </div>
          )}
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-blue-600" />
            Recent Orders
          </h2>
          {analytics?.recentOrders?.length > 0 ? (
            <div className="space-y-3">
              {analytics.recentOrders.map((order) => (
                <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800">{order.orderNumber || `#${order.id}`}</p>
                    <p className="text-sm text-gray-500 truncate">{order.customerName}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-700">{formatCurrency(order.totalAmount)}</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                      {getStatusIcon(order.status)}
                      <span className="capitalize">{order.status}</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No recent orders</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expiry Loss Breakdown */}
      {lossData?.byProduct?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Expiry Loss Breakdown
            </h2>
            <div className="text-right">
              <p className="text-sm text-gray-500">All-time total loss</p>
              <p className="text-lg font-bold text-red-600">{formatCurrency(lossData.allTime.totalLoss)}</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Product</th>
                  <th className="text-right py-3 px-3 font-medium text-gray-600">Price/Unit</th>
                  <th className="text-right py-3 px-3 font-medium text-gray-600">Units Expired</th>
                  <th className="text-right py-3 px-3 font-medium text-gray-600">Loss Amount</th>
                  <th className="text-right py-3 px-3 font-medium text-gray-600">Waste %</th>
                  <th className="text-right py-3 px-3 font-medium text-gray-600">Batches</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lossData.byProduct.map((row) => (
                  <tr key={row.productId} className="hover:bg-gray-50">
                    <td className="py-3 px-3 font-medium text-gray-800">{row.productName}</td>
                    <td className="py-3 px-3 text-right text-gray-600">{formatCurrency(row.pricePerUnit)}</td>
                    <td className="py-3 px-3 text-right text-gray-600">{formatNumber(row.totalUnitsExpired)}</td>
                    <td className="py-3 px-3 text-right font-semibold text-red-600">{formatCurrency(row.lossAmount)}</td>
                    <td className="py-3 px-3 text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        row.wastePercentage >= 50 ? 'bg-red-100 text-red-700' :
                        row.wastePercentage >= 25 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {row.wastePercentage}%
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right text-gray-500">{row.batchCount}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50">
                  <td className="py-3 px-3 font-bold text-gray-800">Total</td>
                  <td className="py-3 px-3"></td>
                  <td className="py-3 px-3 text-right font-bold text-gray-800">{formatNumber(lossData.allTime.totalUnits)}</td>
                  <td className="py-3 px-3 text-right font-bold text-red-600">{formatCurrency(lossData.allTime.totalLoss)}</td>
                  <td className="py-3 px-3"></td>
                  <td className="py-3 px-3 text-right text-gray-500">{lossData.allTime.batchCount}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {lossData.allTime.totalLoss > 0 && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Tip:</strong> Products with high waste percentage may need shorter restock cycles or lower shelf life values to reduce losses.
              </p>
            </div>
          )}
        </div>
      )}

      {/* User Stats */}
      {userStats && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            User Overview
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-800">{formatNumber(userStats.total)}</p>
              <p className="text-sm text-gray-500">Total Users</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{formatNumber(userStats.active)}</p>
              <p className="text-sm text-gray-500">Active Users</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{formatNumber(userStats.admins)}</p>
              <p className="text-sm text-gray-500">Admin Users</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{formatNumber(userStats.newThisMonth)}</p>
              <p className="text-sm text-gray-500">New This Month</p>
            </div>
          </div>
        </div>
      )}

      {/* Download Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#548B2E]/10 rounded-lg">
                <FileSpreadsheet className="w-6 h-6 text-[#548B2E]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">Download Analytics Report</h2>
                <p className="text-sm text-gray-500">Select a date range for your report</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="reportStartDate" className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="reportStartDate"
                    type="date"
                    value={reportStartDate}
                    onChange={(e) => setReportStartDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-[#548B2E] outline-none"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="reportEndDate" className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="reportEndDate"
                    type="date"
                    value={reportEndDate}
                    onChange={(e) => setReportEndDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-[#548B2E] outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowReportModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
                disabled={reportLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDownloadReport}
                disabled={reportLoading || !reportStartDate || !reportEndDate}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reportLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Download CSV
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsManagement;
